import { config } from "process";
import {
  LineHandling,
  Activity,
  ProcessConfig,
  Nomination,
  ProcessFlowConfig,
  ActivityLog,
  ActivitiesResult,
  TruckNomination
} from "../models";
import { WorkPacket, WorkPacketValuesModel } from "../models/WorkPacket";
import { WorkPacket as TruckWorkPacket } from "../models/TruckWorkPacket";

export class PersistentDataService {
  public static getActionTimeString(d: Date): string {
    if (d) return d.toISOString();
    return new Date().toISOString();
  }

  public static async saveLineHandling(lineHandling: LineHandling) {
    let cache = await caches.open("sof");
    const encodedLine = encodeURIComponent(
      lineHandling.lineHandlingNo.toLowerCase()
    );
    cache.delete(`/api/linehandling/${encodedLine}`);

    cache.put(
      `/api/linehandling/${encodedLine}`,
      new Response(JSON.stringify(lineHandling))
    );
  }

  public static async updateNomination(
    lineHandling: LineHandling,
    nomination: Nomination
  ): Promise<LineHandling> {
    let nom = lineHandling.nominations.find(
      (f) =>
        f.nominationNo.toLowerCase() === nomination.nominationNo.toLowerCase()
    );
    if (nom) {
      const index = lineHandling.nominations.indexOf(nom);
      if (index >= 0) {
        lineHandling.nominations = [
          ...lineHandling.nominations.slice(0, index),
          nomination,
          ...lineHandling.nominations.slice(index + 1)
        ];
      }

      await this.saveLineHandling(lineHandling);
    }
    return lineHandling;
  }

  public static async saveActivities(
    lineHandlingNo: string,
    activities: Activity[]
  ) {
    let cache = await caches.open("sof");
    const encodedLine = encodeURIComponent(lineHandlingNo.toLowerCase());
    await cache.put(
      `/api/linehandling/${encodedLine}/activities`,
      new Response(JSON.stringify(activities), {
        headers: { "Content-Type": "application/json" }
      })
    );
  }

  public static async insertOrUpdateActivity(
    lineHandlingNo: string,
    activity: Activity
  ) {
    const cache = await caches.open("sof");
    const encodedLine = encodeURIComponent(lineHandlingNo.toLowerCase());
    const response = await cache.match(
      `/api/linehandling/${encodedLine}/activities`
    );
    if (response) {
      const activities = (await response.json()) as Activity[];
      let result: Activity[] = [];
      const index = activities.findIndex((f) => f.id === activity.id);
      if (index === -1) result = [...activities, activity];
      else
        result = [
          ...activities.slice(0, index),
          activity,
          ...activities.slice(index + 1)
        ];
      if (result.length > 0) {
        await this.saveActivities(lineHandlingNo, result);
      }
    } else {
      await this.saveActivities(lineHandlingNo, [activity]);
    }
  }

  // Get the LineHandling from cache
  public static async getLineHandlingAsync(
    lineHandlingNo: string
  ): Promise<LineHandling | undefined> {
    const cache = await caches.open("sof");
    const encodedLine = encodeURIComponent(lineHandlingNo.toLowerCase());
    let response = await cache.match(`/api/linehandling/${encodedLine}`);
    if (response) return (await response.json()) as LineHandling;
  }

  public static async deleteLineHandlingAsync(lineHandlingNo: string) {
    const cache = await caches.open("sof");
    const encodedLine = encodeURIComponent(lineHandlingNo.toLowerCase());
    await cache.delete(`/api/linehandling/${encodedLine}`);
  }

  // Get the activities by LineHandling from Cache
  public static async getActivities(
    lineHandlingNo: string
  ): Promise<Activity[] | undefined> {
    const cache = await caches.open("sof");
    const encodedLine = encodeURIComponent(lineHandlingNo.toLowerCase());
    let response = await cache.match(
      `/api/linehandling/${encodedLine}/activities`
    );
    if (response) return (await response.json()) as Activity[];
  }

  public static async getOfflineLineHandling(
    status: string
  ): Promise<LineHandling[]> {
    const statusCode = ["Expected", "Arrived", "EndConference", "Departed"];
    const cache = await caches.open("sof");
    var keys = await cache.keys();
    let result: LineHandling[] = [];
    for (const key of keys) {
      if (
        key.url.indexOf("/api/linehandling/") > 0 &&
        key.url.indexOf("comment") < 0
      ) {
        const response = await cache.match(key);
        if (response) {
          const line = (await response.json()) as LineHandling;
          if (
            line.status === statusCode.indexOf(status) ||
            (status === "Arrived" && line.status === 2)
          ) {
            result = [...result, line];
          }
        }
      }
    }
    return result;
  }

  public static async getOfflineNomination(
    status: string
  ): Promise<TruckNomination[]> {
    const statusCode = [
      "Expected",
      "Arrived",
      "Accepted",
      "Rejected",
      "Departed"
    ];
    const cache = await caches.open("sof");
    var keys = await cache.keys();
    let result: TruckNomination[] = [];
    for (const key of keys) {
      if (
        key.url.indexOf("/api/nomination/") > 0 &&
        key.url.indexOf("comment") < 0
      ) {
        const response = await cache.match(key);
        if (response) {
          const line = (await response.json()) as TruckNomination;
          if (
            line.status === statusCode.indexOf(status) ||
            (status === "Arrived" && line.status === 2)
          ) {
            result = [...result, line];
          }
        }
      }
    }
    return result;
  }

  // Get the process flow configuration
  public static async getConfigurationAsync(): Promise<
    ProcessConfig | undefined
  > {
    const cache = await caches.open("sof");
    let response = await cache.match(`/api/configuration/default`);
    if (response) {
      const result = await response.json();
      return result as ProcessConfig;
    }
  }

  public static async saveProcessFlowConfigurationsAsync(
    configs: ProcessFlowConfig[]
  ) {
    const cache = await caches.open("sof");
    const url = "/api/configuration";
    cache.delete(url);
    cache.put(url, new Response(JSON.stringify(configs)));
  }

  public static async getProcessFlowConfigurationsAsync(): Promise<
    ProcessFlowConfig[] | undefined
  > {
    const cache = await caches.open("sof");
    const url = "/api/configuration";
    const response = await cache.match(url);
    if (response) {
      const result = await response.json();
      console.log("getProcessFlowConfigurationsAsync", result);
      return result as ProcessFlowConfig[];
    }
  }

  public static async saveActivityLogsAsync(
    lineHandlingNo: string,
    activityLogs: ActivityLog[]
  ) {
    const cache = await caches.open("sof");
    const url =
      `/api/v1.5/linehandling/${lineHandlingNo}/activities`.toLowerCase();
    cache.delete(url);
    const payload = { activities: activityLogs } as ActivitiesResult;
    cache.put(url, new Response(JSON.stringify(payload)));
  }

  public static async getActivityLogsAsync(
    lineHandlingNo: string
  ): Promise<ActivityLog[] | undefined> {
    const cache = await caches.open("sof");
    const url =
      `/api/v1.5/linehandling/${lineHandlingNo}/activities`.toLowerCase();
    const response = await cache.match(url);
    if (response) {
      const result = (await response.json()) as ActivitiesResult;
      return result.activities;
    }
  }

  public static async saveAllWorkPacketsAsync(
    configs: WorkPacket[] | TruckWorkPacket[]
  ) {
    const cache = await caches.open("sof");
    const url = "/api/workpacket/all";
    cache.delete(url);
    cache.put(url, new Response(JSON.stringify(configs)));
  }

  public static async saveWorkPacketValuesAsync(
    lineHandlingNo: string,
    workPacketValues: WorkPacketValuesModel[]
  ) {
    const cache = await caches.open("sof");
    const url = `/api/workpacket/${lineHandlingNo}/values`;
    cache.delete(url);
    cache.put(url, new Response(JSON.stringify(workPacketValues)));
  }
}
